import { api } from '../client';
import type { MoneyBreakdown } from '@/lib/utils';

export interface Supplier {
  id: string;
  name: string;
  code: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: {
    street?: string;
    city?: string;
    state?: string;
    country?: string;
    postalCode?: string;
  };
  rating?: number;
  isActive: boolean;
  _count?: { purchaseOrders: number };
  createdAt: string;
  updatedAt: string;
}

export interface CreateSupplierDto {
  name: string;
  code: string;
  contactName?: string;
  email?: string;
  phone?: string;
  address?: Supplier['address'];
  rating?: number;
  isActive?: boolean;
}

export interface QuerySupplierParams {
  search?: string;
  isActive?: boolean;
  page?: number;
  limit?: number;
}

export interface SupplierPerformance {
  supplierId: string;
  supplierName: string;
  metrics: {
    totalOrders: number;
    /** Null when this supplier's orders span currencies with no rate on file. */
    totalValue: number | null;
    currency: string;
    spendByCurrency: MoneyBreakdown[];
    unconvertedCurrencies: string[];
    /** Null when no order can be judged for punctuality. */
    onTimeDeliveryRate: number | null;
    onTimeBasis: string;
    fulfillmentRate: number;
    currentRating?: number;
  };
}

export const suppliersApi = {
  async getAll(params?: QuerySupplierParams) {
    const response = await api.get<{
      data: Supplier[];
      meta: { total: number; page: number; limit: number; totalPages: number };
    }>('/suppliers', { params });
    return response.data;
  },

  async getById(id: string) {
    const response = await api.get<Supplier>(`/suppliers/${id}`);
    return response.data;
  },

  async getPurchaseOrders(id: string, params?: { page?: number; limit?: number }) {
    const response = await api.get(`/suppliers/${id}/purchase-orders`, { params });
    return response.data;
  },

  async getPerformance(id: string) {
    const response = await api.get<SupplierPerformance>(`/suppliers/${id}/performance`);
    return response.data;
  },

  async create(data: CreateSupplierDto) {
    const response = await api.post<Supplier>('/suppliers', data);
    return response.data;
  },

  async update(id: string, data: Partial<CreateSupplierDto>) {
    const response = await api.patch<Supplier>(`/suppliers/${id}`, data);
    return response.data;
  },

  async delete(id: string) {
    const response = await api.delete<Supplier>(`/suppliers/${id}`);
    return response.data;
  },
};
